<!--
  ~ Copyright (c) 2024-2026 GM Global Technology Operations LLC. All rights reserved.
  ~ This information is confidential and proprietary to GM Global Technology
  ~ Operations LLC and may not be used, modified, copied or distributed.
  -->
<br/>
@if (permission()) {
<table style="min-height: 80px; margin-top: -20px; margin-bottom: -10px;">
  <tr>
    <td>
      <h1 class="page-header">{{ getGridDisplayName() }} Grid</h1>
    </td>
    <td>
      <!-- FILTER DISPLAY BLOCK -->
      @if (activeFiltersArray.length > 0) {
        <div class="search-criteria">
          <div class="search-criteria-title">&nbsp; Active Search Criteria</div>
          @for (f of activeFiltersArray; track f) {
            <span class="search-criteria-element" [class.search-criteria-element-locked]="isLockedFilter(f.key)">
              <b>{{ f.key }}:</b> {{ f.value.join(', ') }}
              <button (click)="removeSearchTerm(f.key)" class="fa fa-times"
                      [disabled]="isLockedFilter(f.key)"
                      [title]="isLockedFilter(f.key) ? 'Required for read-only users' : 'Clear filter'"></button>
            </span>
          }
          @if (removableFilterCount() >= 2) {
            <button class="search-criteria-element search-criteria-element-remove-all"
              (click)="removeAllSearchTerms()"
              title="Clear all search filters">
              <i class="fa fa-times"></i>
              Clear All
            </button>
          }
        </div>
      }
    </td>
  </tr>
</table>
  <div>
    <table style="width: 100%;">
      <tr>
        <td>
          <p-confirmDialog key="updateUserConfirmation"></p-confirmDialog>
        </td>
        <td>
            <div class="buttons pull-right">
                @if (componentName === 'RingTerminal') {
                    <p-button icon="pi pi-question-circle" label="More Information"
                              title="New Terminal / Modify a Terminal / Strategy / Best Practices / Requirements"
                              (click)="onCustomButtonClick('https://e2ks.gm.com/e2ks/kpac/view/ELEC86-43/latest#details')"
                              class="gm-btn-primary mr-2"></p-button>
                }
                @if (showAdvancedFilter) {
                    <p-button label="Advanced Filter" (click)="advancedFilter()"
                              icon="pi pi-filter" class="gm-btn-primary mr-2"></p-button>
                }
                @if (adminPermission() && !serverPaged()) {
                    <!-- Server-paged catalogs get "+ Add" inside the ag-Grid-lookalike paging panel below; a
                         client-paged catalog renders AG Grid's own native panel instead, which has no room for a
                         custom button, so the same action is offered here so the capability is not lost. -->
                    <p-button label="Add New" (click)="onAddNewClick()" icon="pi pi-plus" class="gm-btn-primary mr-2"></p-button>
                }
                @if (adminPermission()) {
                    <p-button label="Save All Changes" (click)="saveAll()" icon="pi pi-save"
                              [disabled]="!dataChanged || duplicate || rowsInvalid"
                              [loading]="!!(saving || exporting || resetting)" class="gm-btn-primary mr-2"></p-button>
                }
                @if (!!(isPurchAdminOnly && hasPPCO)) {
                    <p-button label="Save PPCO Changes" (click)="savePPCOChanges()" icon="pi pi-save"
                              [disabled]="!ppcoDataChanged"
                              [loading]="!!(saving || exporting)" class="gm-btn-primary mr-2"></p-button>
                }
                @if (!!(isTierNAdminOnly && hasTierNResilient)) {
                    <p-button label="Save Tier-N Changes" (click)="saveTierNChanges()" icon="pi pi-save"
                              [disabled]="!tierNDataChanged"
                              [loading]="!!(saving || exporting)" class="gm-btn-primary mr-2"></p-button>
                }
                @if (canExport()) {
                    <p-button icon="pi pi-download" (onClick)="exportReport()"
                              label="Download Report" class="gm-btn-primary mr-2"></p-button>
                    <p-button icon="pi pi-download" (onClick)="exportHistory()"
                              label="Download History" class="gm-btn-primary mr-2"></p-button>
                }
                @if (adminPermission()) {
                    <p-button label="Reset Changes" (click)="resetChanges()"
                              [loading]="!!(saving || exporting || resetting)"
                              severity="warn" icon="pi pi-replay"></p-button>
                }
                @if (!!(isPurchAdminOnly && hasPPCO)) {
                    <p-button label="Revert PPCO Changes" (click)="revertPPCOChanges()"
                              [disabled]="!ppcoDataChanged"
                              [loading]="!!(saving || exporting)"
                              severity="warn" icon="pi pi-replay"></p-button>
                }
                @if (!!(isTierNAdminOnly && hasTierNResilient)) {
                    <p-button label="Revert Tier-N Changes" (click)="revertTierNChanges()"
                              [disabled]="!tierNDataChanged"
                              [loading]="!!(saving || exporting)"
                              severity="warn" icon="pi pi-replay"></p-button>
                }
            </div>
        </td>
      </tr>
    </table>
    <p-confirmDialog key="deleteRowConfirmation"></p-confirmDialog>
    <p-confirmDialog key="serverPagingConfirmation"></p-confirmDialog>
      <div class="component-catalog-grid-wrapper">
    <ag-grid-angular #agGrid [gridOptions]="gridOptions"
      style="width: 100%; height: 600px;" class="ag-theme-balham componentCatalog"
      [undoRedoCellEditing]="true" [enableCellTextSelection]="true"
      (cellEditingStopped)="cellEditingStopped($event, rowData)"
      (filterChanged)="onFilterChanged()"
                         (columnResized)="onColumnResized()"
                         (sortChanged)="onSortChanged()"
      [undoRedoCellEditingLimit]="50" [columnDefs]="cols" [rowData]="rowDataSignal()"
                         [defaultColDef]="defaultColDef" [autoSizeStrategy]="autoSizeStrategy"
                         (gridReady)="onGridReady($event)" [rowHeight]="60"
    [pagination]="!serverPaged()" [paginationPageSize]="paginationPageSize"
    [paginationPageSizeSelector]="paginationPageSizeSelector"
    [rowClassRules]="rowClassRules"></ag-grid-angular>
        @if (serverPaged()) {
        <!-- Only one page of rows is ever loaded, so AG Grid cannot draw its paging panel: it counts the rows it
             holds. This is that panel, in its own markup, reporting what the server said instead. The theme and
             direction classes are repeated because the panel sits outside the grid element that carries them -
             ag-ltr is not decorative, it is what ag-grid.css scopes the label gap, the picker's inner padding and
             the dropdown item indent to (see the .ag-ltr .ag-label / .ag-ltr .ag-select rules); dropping it collapses
             all three. -->
        <div class="ag-theme-balham ag-ltr server-paging-panel">
          <div class="ag-paging-panel ag-unselectable">
            @if (adminPermission()) {
              <button type="button" class="ag-grid-add-row-pagination-btn" title="Insert a new row"
                      (click)="onAddNewClick()">+ Add</button>
            }
            <span class="ag-paging-page-size">
              <div class="ag-picker-field ag-labeled ag-label-align-left ag-select" role="presentation">
                <div class="ag-label">Page Size:</div>
                <div class="ag-wrapper ag-picker-field-wrapper"
                     [class.ag-picker-collapsed]="!pageSizePickerOpen()"
                     [class.ag-picker-expanded]="pageSizePickerOpen()"
                     tabindex="0" role="combobox" aria-label="Page Size"
                     [attr.aria-expanded]="pageSizePickerOpen()"
                     (click)="togglePageSizePicker()" (keydown)="onPageSizePickerKeyDown($event)"
                     (focusout)="onPageSizePickerFocusOut($event)">
                  <div class="ag-picker-field-display">{{ serverPageSize() }}</div>
                  <div class="ag-picker-field-icon" aria-hidden="true">
                    <span class="ag-icon ag-icon-small-down" role="presentation"></span>
                  </div>
                </div>
                @if (pageSizePickerOpen()) {
                  <div class="ag-select-list ag-list" role="listbox" aria-label="Page Size">
                    @for (size of paginationPageSizeSelector; track size; let i = $index) {
                      <div class="ag-list-item ag-select-list-item" role="option"
                           [class.ag-active-item]="i === pageSizePickerHighlight()"
                           [attr.aria-selected]="size === serverPageSize()"
                           (mousedown)="$event.preventDefault(); changeServerPageSize(size)">
                        <span>{{ size }}</span>
                      </div>
                    }
                  </div>
                }
              </div>
            </span>
            <!-- &ngsp; keeps the spaces AG Grid gets from the whitespace in its own markup: Angular drops text nodes
                 that hold nothing else, which would read as "1to20of953". -->
            <span class="ag-paging-row-summary-panel" role="status">
              <span class="ag-paging-row-summary-panel-number">{{ firstRowOnPage() }}</span>
              <span>&ngsp;to&ngsp;</span>
              <span class="ag-paging-row-summary-panel-number">{{ lastRowOnPage() }}</span>
              <span>&ngsp;of&ngsp;</span>
              <span class="ag-paging-row-summary-panel-number">{{ serverTotalRows() }}</span>
            </span>
            <span class="ag-paging-page-summary-panel" role="presentation">
              <div class="ag-button ag-paging-button" role="button" aria-label="First Page" tabindex="0"
                   [class.ag-disabled]="onFirstServerPage()" [attr.aria-disabled]="onFirstServerPage()"
                   (click)="goToFirstServerPage()" (keydown.enter)="goToFirstServerPage()"
                   (keydown.space)="$event.preventDefault(); goToFirstServerPage()">
                <span class="ag-icon ag-icon-first" role="presentation"></span>
              </div>
              <div class="ag-button ag-paging-button" role="button" aria-label="Previous Page" tabindex="0"
                   [class.ag-disabled]="onFirstServerPage()" [attr.aria-disabled]="onFirstServerPage()"
                   (click)="goToPreviousServerPage()" (keydown.enter)="goToPreviousServerPage()"
                   (keydown.space)="$event.preventDefault(); goToPreviousServerPage()">
                <span class="ag-icon ag-icon-previous" role="presentation"></span>
              </div>
              <span class="ag-paging-description" role="status">
                <span>Page&ngsp;</span>
                <span class="ag-paging-number">{{ currentServerPage() }}</span>
                <span>&ngsp;of&ngsp;</span>
                <span class="ag-paging-number">{{ totalServerPages() }}</span>
              </span>
              <div class="ag-button ag-paging-button" role="button" aria-label="Next Page" tabindex="0"
                   [class.ag-disabled]="onLastServerPage()" [attr.aria-disabled]="onLastServerPage()"
                   (click)="goToNextServerPage()" (keydown.enter)="goToNextServerPage()"
                   (keydown.space)="$event.preventDefault(); goToNextServerPage()">
                <span class="ag-icon ag-icon-next" role="presentation"></span>
              </div>
              <div class="ag-button ag-paging-button" role="button" aria-label="Last Page" tabindex="0"
                   [class.ag-disabled]="onLastServerPage()" [attr.aria-disabled]="onLastServerPage()"
                   (click)="goToLastServerPage()" (keydown.enter)="goToLastServerPage()"
                   (keydown.space)="$event.preventDefault(); goToLastServerPage()">
                <span class="ag-icon ag-icon-last" role="presentation"></span>
              </div>
            </span>
          </div>
        </div>
        }
      </div>
    </div>
}